/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { FormattedHTMLMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Divider, Grid, Typography2 as Typography, breakpoints } from '@coursera/cds-core';
import { ArrowUpIcon } from '@coursera/cds-icons';

import RelatedDegree from 'bundles/premium-hub/components/career-module/RelatedDegree';
import RoleTestimonial from 'bundles/premium-hub/components/career-module/RoleTestimonial';
import SalaryBar from 'bundles/premium-hub/components/career-module/SalaryBar';
import type { CareerRolesModuleItem } from 'bundles/premium-hub/types';

import _t from 'i18n!nls/premium-hub';

type Props = {
  role?: CareerRolesModuleItem | null;
};

const styles = {
  contentContainer: css`
    display: flex;
    flex-direction: row-reverse;
    gap: var(--cds-spacing-1400);

    ${breakpoints.down('sm')} {
      flex-direction: column;
      gap: var(--cds-spacing-400);
    }
  `,
  testimonialContainer: css`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--cds-spacing-400);
  `,
  divider: css`
    margin: var(--cds-spacing-300) 0;
  `,
  jobPostingsContainer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    padding-top: var(--cds-spacing-150);
  `,
  jobPostingsIcon: css`
    color: var(--cds-color-feedback-success);
  `,
  salaryContainer: css`
    flex: 1;
  `,
  salaryBarsContainer: css`
    margin-top: calc(var(--cds-spacing-200) + var(--cds-spacing-25));
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
};

const CareerRolePanel = ({ role }: Props) => {
  const baseSalaryBarWidth = 60;
  const roleSalaryBarWidth = useMemo(() => {
    const baseMedianSalary = role?.parentRoleJobPostingData?.medianSalary;
    const roleMedianSalary = role?.jobPostingData?.medianSalary;

    if (!baseMedianSalary || !roleMedianSalary) return baseSalaryBarWidth;

    return Math.min((roleMedianSalary / baseMedianSalary) * baseSalaryBarWidth, 100);
  }, [role?.jobPostingData, role?.parentRoleJobPostingData]);

  if (!role) return null;

  const {
    roleDescription,
    testimonialImage,
    testimonialHeading,
    testimonialBody,
    relatedDegree,
    parentRoleJobPostingData,
    jobPostingData,
  } = role;

  const numJobsAvailable = jobPostingData?.numJobsAvailable ?? 0;
  const hasJobPostings = numJobsAvailable > 0;

  return (
    <Grid container spacing={32}>
      <Grid item xs={12} md={6}>
        <Typography variant="bodyPrimary" component="p" color="supportText">
          {roleDescription}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <div css={styles.contentContainer}>
          <div css={styles.salaryContainer}>
            <Typography variant="subtitleMedium" component="p">
              {_t('Median salary')}
            </Typography>
            <div css={styles.salaryBarsContainer}>
              <SalaryBar
                roleTitle={_t('Working professional')}
                salary={parentRoleJobPostingData?.medianSalary}
                currencyCode={parentRoleJobPostingData?.currencyCode}
                width={baseSalaryBarWidth}
                variant="base"
              />
              <SalaryBar
                roleTitle={role.roleName ?? ''}
                salary={jobPostingData?.medianSalary}
                currencyCode={jobPostingData?.currencyCode}
                width={roleSalaryBarWidth}
                variant="career"
              />
            </div>
            {hasJobPostings && (
              <React.Fragment>
                <Divider color="light" orientation="horizontal" css={styles.divider} />
                <Typography variant="subtitleMedium" component="p">
                  {_t('Job postings¹')}
                </Typography>
                <div css={styles.jobPostingsContainer}>
                  <ArrowUpIcon size="large" css={styles.jobPostingsIcon} />
                  <Typography variant="bodyPrimary" component="span">
                    <strong>
                      <FormattedHTMLMessage
                        message={_t('{jobPostings} jobs in the past 12 months')}
                        jobPostings={<FormattedNumber value={numJobsAvailable} />}
                      />
                    </strong>
                  </Typography>
                </div>
              </React.Fragment>
            )}
          </div>
          <div css={styles.testimonialContainer}>
            <RoleTestimonial imageUrl={testimonialImage?.url} heading={testimonialHeading} body={testimonialBody} />
            <RelatedDegree degree={relatedDegree} />
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

export default CareerRolePanel;
